import * as Sentry from "@sentry/react";
import type { CoworkingScopeSnapshot } from "@conciergeco/rpc-contract/coworking";

const emptyScopeTagValue = "none";
const sentryScopeContextName = "conciergeco.scope";

type ScopeTagValue = string;

type SentryScopeTags = {
  organizationId: ScopeTagValue;
  locationId: ScopeTagValue;
  scopeResolved: "true" | "false";
};

type SentryScopeContext = {
  organizationId: string | null;
  locationId: string | null;
  organizationRole: string | null;
  roles: string[];
  resolved: boolean;
};

type SentryScopeIdentity = {
  tags: SentryScopeTags;
  context: SentryScopeContext;
};

function toTagValue(value: string | null | undefined) {
  return value ?? emptyScopeTagValue;
}

function deriveSentryScopeIdentity(
  scope: CoworkingScopeSnapshot,
): SentryScopeIdentity {
  const organization = scope.current.organization;
  const location = scope.current.location;
  const resolved = Boolean(
    organization &&
    location &&
    location.organizationId === organization.id &&
    scope.authorization.organizationIds.includes(organization.id) &&
    scope.authorization.locationIds.includes(location.id),
  );

  return {
    tags: {
      organizationId: toTagValue(organization?.id),
      locationId: toTagValue(location?.id),
      scopeResolved: resolved ? "true" : "false",
    },
    context: {
      organizationId: organization?.id ?? null,
      locationId: location?.id ?? null,
      organizationRole: organization?.role ?? null,
      roles: [...scope.roles],
      resolved,
    },
  };
}

function applySentryUserIdentity(userId: string) {
  Sentry.setUser({ id: userId });
}

function clearSentryUserIdentity() {
  Sentry.setUser(null);
}

function applySentryScopeContext(scope: CoworkingScopeSnapshot) {
  const identity = deriveSentryScopeIdentity(scope);

  Sentry.setTags(identity.tags);
  Sentry.setContext(sentryScopeContextName, identity.context);
}

function clearSentryScopeContext() {
  Sentry.setTags({
    organizationId: emptyScopeTagValue,
    locationId: emptyScopeTagValue,
    scopeResolved: "false",
  });
  Sentry.setContext(sentryScopeContextName, null);
}

function clearSentryIdentityContext() {
  clearSentryUserIdentity();
  clearSentryScopeContext();
}

export {
  applySentryScopeContext,
  applySentryUserIdentity,
  clearSentryIdentityContext,
  clearSentryScopeContext,
  clearSentryUserIdentity,
  deriveSentryScopeIdentity,
  emptyScopeTagValue,
  sentryScopeContextName,
  type SentryScopeContext,
  type SentryScopeIdentity,
  type SentryScopeTags,
};
